<template>
  <section v-if="allowed" ref="returnTarget" tabindex="-1" :aria-label="$t(`${copyKey}.title`)" class="ncrm-invitation-action d-flex justify-end mb-3">
    <v-btn ref="trigger" variant="tonal" prepend-icon="mdi-email-arrow-right-outline" :disabled="disabled" @click="open = true">
      {{ $t(`${copyKey}.title`) }}
    </v-btn>
    <v-snackbar v-model="success" color="success" :timeout="5000">{{ $t(`${copyKey}.success`) }}</v-snackbar>
    <teleport to="body">
      <v-navigation-drawer v-model="open" location="right" temporary :width="520" class="ncrm-invitation-drawer"
        role="dialog" :aria-modal="open" :aria-label="$t(`${copyKey}.title`)" @keydown.esc="open = false">
        <div v-if="open" class="pa-5 d-flex flex-column ga-4" @keydown.tab="trapFocus">
          <div class="d-flex align-center justify-space-between ga-3">
            <h3 class="text-h6">{{ $t(`${copyKey}.title`) }}</h3>
            <v-btn ref="close" icon="mdi-close" variant="text" :aria-label="$t('app.actions.close')" @click="open = false" />
          </div>
          <p class="text-body-2 mb-0">{{ $t(`${copyKey}.hint`) }}</p>
          <v-skeleton-loader v-if="loading" type="list-item-two-line@3" />
          <v-alert v-else-if="loadError" type="error">
            {{ $t(`${copyKey}.loadError`) }}
            <v-btn variant="text" @click="loadOptions">{{ $t('ncrm.contacts.dataProtection.record.retry') }}</v-btn>
          </v-alert>
          <template v-else>
            <v-alert v-if="!points.length || !purposes.length" type="info">
              {{ $t(`${copyKey}.${!points.length ? 'emptyEmail' : 'emptyPurpose'}`) }}
            </v-alert>
            <v-select v-model="form.contact_point_id" :items="points" item-title="value_display" item-value="id"
              :label="$t(`${copyKey}.email`)" :disabled="sending" :menu-props="{ zIndex: 3220 }" />
            <v-select v-model="form.purpose_id" :items="purposes" item-title="name" item-value="id"
              :label="$t(`${copyKey}.purpose`)" :disabled="sending" :menu-props="{ zIndex: 3220 }"
              @update:model-value="usePurposeDefault" />
            <NcrmPrivacyPurposeLink :purpose-id="form.purpose_id" :return-to="returnTo" />
            <v-select v-model="form.locale" :items="locales.map(value => ({ title: value.toUpperCase(), value }))" :label="$t(`${copyKey}.language`)"
              :disabled="sending" :menu-props="{ zIndex: 3220 }" />
            <v-checkbox v-model="form.doi_after_accept" :label="$t(`${copyKey}.doi`)" :disabled="sending" hide-details />
            <v-progress-linear v-if="checking" indeterminate :aria-label="$t(`${copyKey}.check`)" />
            <v-alert v-if="held" type="warning" role="status">
              {{ $t(`${copyKey}.${sending ? 'pending' : 'uncertain'}`) }}
              <span v-if="held.attempt">{{ $t(`${copyKey}.attempt`, { id: held.attempt }) }}</span>
            </v-alert>
            <v-alert v-else-if="reason" type="warning" role="status">{{ reasonText(reason) }}</v-alert>
            <v-btn v-if="!preview && !checking && !held && form.contact_point_id && form.purpose_id" variant="text" @click="loadPreview">
              {{ $t('ncrm.contacts.dataProtection.record.retry') }}
            </v-btn>
            <v-alert v-if="preview?.warning" type="warning">{{ $t(`${copyKey}.warning`) }}</v-alert>
            <div v-if="preview?.limits" class="text-body-2">
              {{ $t(`${copyKey}.limits`, { used: preview.limits.sends_used ?? '—', max: preview.limits.max_sends }) }}
            </div>
            <router-link v-if="manager" :to="noticesLink">{{ $t(`${copyKey}.notices`) }}</router-link>
            <router-link v-if="smtpAllowed" :to="smtpLink" target="_blank" rel="noopener">{{ $t('ncrm.contacts.dataProtection.record.doiResend.configureSmtp') }}</router-link>
            <div class="d-flex flex-wrap justify-end ga-2">
              <v-btn variant="text" @click="open = false">{{ $t('app.actions.cancel') }}</v-btn>
              <v-btn color="primary" :loading="sending" :disabled="!canSend" @click="send">{{ $t(`${copyKey}.send`) }}</v-btn>
            </div>
          </template>
        </div>
      </v-navigation-drawer>
    </teleport>
  </section>
</template>

<script>
import axios from "axios";
import NcrmPrivacyPurposeLink from "@/components/ncrm/privacy/NcrmPrivacyPurposeLink.vue";
import { hasPrivacyManagerRole, hasPrivacyRegistryRole } from "@/Utile/ncrmPrivacyAccess.mjs";
import { canAccessCompanySmtpWorkspace } from "@/Utile/ncrmMitpSync.mjs";
import { privacyNoticesRoute } from "@/Utile/ncrmExternalFormPrivacy.mjs";
import { invitationReasonText } from "@/Utile/ncrmPrivacyInvitationReasons.mjs";
// Server-owned permission/quota/transport. Holds survive closing/reopening this instance;
// a lost POST is NEVER retried automatically. Generations protect options, preview and saves.
export default {
  name: "NcrmPrivacyInvitationAction",
  components: { NcrmPrivacyPurposeLink },
  props: { subjectType: { type: String, required: true }, subjectId: { type: [String, Number], required: true },
    disabled: { type: Boolean, default: false } },
  emits: ["sent"],
  data: () => ({ open: false, success: false, loading: false, loadError: false, checking: false,
    allowed: false, manager: false, smtpAllowed: false, points: [], purposes: [], locales: [],
    form: {}, preview: null, previewKey: "", resultReason: null, generation: 0, subjectGeneration: 0, previewSequence: 0, holds: {} }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.record.invitation"; },
    subjectKey() { return `${this.subjectType}:${this.subjectId}`; },
    params() { return { subject_type: this.subjectType, subject_id: Number(this.subjectId), ...this.form }; },
    choiceKey() { return JSON.stringify(this.params); },
    pairKey() { return `${this.subjectKey}:${this.form.contact_point_id}:${this.form.purpose_id}`; },
    held() { return this.holds[this.pairKey]; },
    sending() { return this.held?.status === "pending"; },
    canSend() { return this.open && this.allowed && !this.disabled && !this.loading && !this.loadError && !this.checking && !this.held
      && this.previewKey === this.choiceKey && this.preview?.eligible === true; },
    reason() { return this.resultReason || this.preview?.reason; },
    returnTo() { return this.$router?.resolve({ path: this.$route.path, query: { ...this.$route.query, privacyInvitation: "1" } }).fullPath; },
    noticesLink() { return privacyNoticesRoute({ purposeId: this.form.purpose_id, locale: this.form.locale, returnTo: this.returnTo }); },
    smtpLink() { return { path: "/ncrm/data-integrations", query: { tab: "smtp", returnTo: this.returnTo } }; },
  },
  created() {
    this.allowed = hasPrivacyRegistryRole(); this.manager = hasPrivacyManagerRole(); this.smtpAllowed = canAccessCompanySmtpWorkspace();
    if (this.allowed && this.$route?.query?.privacyInvitation === "1") {
      this.open = true;
      const query = { ...this.$route.query }; delete query.privacyInvitation; this.$router.replace({ query });
    }
  },
  beforeUnmount() { this.subjectGeneration += 1; this.invalidate(); },
  watch: {
    subjectKey() { this.subjectGeneration += 1; this.open = false; this.success = false; this.invalidate(); },
    open(value) {
      this.invalidate();
      if (value) { this.loadOptions(); this.$nextTick(() => this.$refs.close?.$el?.focus()); }
      else this.$nextTick(() => {
        if (!this.open) (this.$refs.trigger?.$el?.disabled ? this.$refs.returnTarget : this.$refs.trigger?.$el)?.focus();
      });
    },
    choiceKey() { this.loadPreview(); },
  },
  methods: {
    trapFocus(event) {
      const nodes = [...event.currentTarget.querySelectorAll('button,a,input,[tabindex="0"]')]
        .filter((node) => !node.disabled && node.getClientRects().length);
      const boundary = event.shiftKey ? nodes[0] : nodes.at(-1);
      if (document.activeElement === boundary) { event.preventDefault(); (event.shiftKey ? nodes.at(-1) : nodes[0])?.focus(); }
    },
    invalidate() {
      this.generation += 1; this.previewSequence += 1; this.preview = null; this.previewKey = "";
      this.resultReason = null; this.form = {}; this.points = []; this.purposes = []; this.locales = [];
      this.loading = false; this.checking = false; this.loadError = false;
    },
    usePurposeDefault() {
      this.form.doi_after_accept = this.purposes.find((purpose) => purpose.id === this.form.purpose_id)?.double_opt_in_enabled === true;
    },
    reasonText(code) {
      return invitationReasonText(this, code, this.preview?.limits);
    },
    async loadOptions() {
      const generation = ++this.generation;
      this.previewSequence += 1; this.preview = null; this.previewKey = "";
      this.loading = true; this.loadError = false;
      try {
        const { data } = await axios.get("/ncrm/privacy/invitations/options", {
          params: { subject_type: this.subjectType, subject_id: Number(this.subjectId) }, timeout: 15000 });
        if (generation !== this.generation || !this.open) return;
        this.points = data.contact_points; this.purposes = data.purposes; this.locales = data.locales;
        this.form = { contact_point_id: this.points.find((point) => point.is_primary)?.id || this.points[0]?.id || null,
          purpose_id: this.purposes.length === 1 ? this.purposes[0].id : null,
          locale: this.locales.includes(this.$i18n.locale) ? this.$i18n.locale : "ro", doi_after_accept: false };
        this.usePurposeDefault();
      } catch { if (generation === this.generation) this.loadError = true; }
      finally { if (generation === this.generation) this.loading = false; }
    },
    async loadPreview() {
      const sequence = ++this.previewSequence, generation = this.generation, key = this.choiceKey;
      this.preview = null; this.previewKey = ""; this.resultReason = null; this.checking = false;
      if (!this.open || !this.form.contact_point_id || !this.form.purpose_id || !this.form.locale) return;
      this.checking = true;
      try {
        const { data } = await axios.get("/ncrm/privacy/invitations/preview", { params: this.params, timeout: 15000 });
        if (generation === this.generation && sequence === this.previewSequence && key === this.choiceKey) {
          this.preview = data; this.previewKey = key;
        }
      } catch { if (generation === this.generation && sequence === this.previewSequence) this.resultReason = "preview_failed"; }
      finally { if (generation === this.generation && sequence === this.previewSequence) this.checking = false; }
    },
    async send() {
      if (!this.canSend) return;
      const generation = this.generation, key = this.choiceKey, pair = this.pairKey, params = { ...this.params };
      const subject = this.subjectKey, subjectGeneration = this.subjectGeneration;
      this.holds[pair] = { status: "pending" }; this.resultReason = null;
      let result;
      try { result = (await axios.post("/ncrm/privacy/invitations/send", params, { timeout: 45000 })).data; }
      catch (error) {
        // Client rejections precede SMTP; 408/5xx may come from a proxy after dispatch.
        const status = error.response?.status;
        result = status >= 400 && status < 500 && status !== 408
          ? { outcome: "not_sent", reason_code: error.response.data?.code === "TENANT_READ_ONLY" ? "tenant_read_only" : "unavailable" }
          : { outcome: "uncertain" };
      }
      const uncertain = !["accepted", "rejected", "not_sent"].includes(result?.outcome)
        || (result.outcome !== "not_sent" && (!result.recorded || !result.links_settled));
      if (uncertain) this.holds[pair] = { status: "uncertain", attempt: result?.attempt_id };
      else delete this.holds[pair];
      if (!uncertain && result.outcome === "accepted" && subject === this.subjectKey && subjectGeneration === this.subjectGeneration) {
        // Dismissal is not cancellation. Report completion without closing a newer drawer.
        if (generation === this.generation && key === this.choiceKey) this.open = false;
        this.success = true; this.$emit("sent"); return;
      }
      if (generation !== this.generation || key !== this.choiceKey || !this.open) return;
      if (uncertain) { this.$nextTick(() => this.$refs.close?.$el?.focus()); return; }
      await this.loadPreview();
      if (generation === this.generation && key === this.choiceKey && this.open) {
        this.resultReason = result.reason_code || "unavailable"; this.$nextTick(() => this.$refs.close?.$el?.focus());
      }
    },
  },
};
</script>

<style scoped>
.ncrm-invitation-action:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: 2px; }
.ncrm-invitation-drawer { z-index: 3210 !important; max-width: 100vw; text-align: left; }
</style>
